import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMembers } from '../../firebase';
import { UserProfile } from '../../models/FirestoreModels';
import { findMentionsInText } from '../../services/notificationCenterService';

interface RenderWithMentionsProps {
  text: string;
  className?: string;
  members?: UserProfile[];
}

let cachedGlobalMembers: UserProfile[] = [];
let isFetchingMembers = false;
const listeners = new Set<(m: UserProfile[]) => void>();

export const RenderWithMentions: React.FC<RenderWithMentionsProps> = ({
  text,
  className = '',
  members: propMembers,
}) => {
  const navigate = useNavigate();
  const [loadedMembers, setLoadedMembers] = useState<UserProfile[]>(() => {
    return propMembers || cachedGlobalMembers;
  });

  useEffect(() => {
    if (propMembers && propMembers.length > 0) {
      setLoadedMembers(propMembers);
      return;
    }

    if (cachedGlobalMembers.length > 0) {
      setLoadedMembers(cachedGlobalMembers);
      return;
    }

    const listener = (newMembers: UserProfile[]) => {
      setLoadedMembers(newMembers);
    };
    listeners.add(listener);

    if (!isFetchingMembers) {
      isFetchingMembers = true;
      getMembers()
        .then((data) => {
          cachedGlobalMembers = data;
          listeners.forEach((l) => l(data));
          listeners.clear();
        })
        .catch((err) => {
          console.warn('[RenderWithMentions] Failed to load members:', err);
        })
        .finally(() => {
          isFetchingMembers = false;
        });
    }

    return () => {
      listeners.delete(listener);
    };
  }, [propMembers]);

  if (!text) return null;

  const membersToUse = propMembers || loadedMembers;
  const mentions = findMentionsInText(text, membersToUse);

  if (mentions.length === 0) {
    return <span className={className}>{text}</span>;
  }

  let lastIndex = 0;
  const elements: React.ReactNode[] = [];

  mentions.forEach((m, idx) => {
    if (m.startIndex > lastIndex) {
      elements.push(text.substring(lastIndex, m.startIndex));
    }

    if (m.member) {
      elements.push(
        <span
          key={`mention-${idx}-${m.startIndex}`}
          className="member-link font-bold cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/members/${m.member!.id}`);
          }}
          title={`View ${m.member.displayName || m.member.nickname || 'Member'}'s profile`}
        >
          {m.raw}
        </span>
      );
    } else {
      elements.push(
        <strong key={`unrec-${idx}-${m.startIndex}`} className="member-link font-bold">
          {m.raw}
        </strong>
      );
    }

    lastIndex = m.endIndex;
  });

  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }

  return <span className={className}>{elements}</span>;
};
